import { useEffect, useRef, useState } from "react";

import { INSIGHT_TYPE, MENTION_TYPE } from "@/constants";
import { useMentionCard } from "@/containers/MentionCard/context";
import { useLocalAudio } from "@/containers/MentionCard/LocalAudioContext";
import useHash from "@/hooks/useHash";
import { cx } from "@/utils";
import UndoRemovedAll from "@components/UndoRemovedAll/index_v4";

import styles from "./MentionCardBase.module.css";
// This is the base for the Desktop episode, it has some elements that all of the Episodes share.
type MentionCardBaseProps = {
  className?: string;
  children?: React.ReactElement;
};

const HIGHLIGHT_DURATION = 3500;

const MentionCardBase: React.FunctionComponent<MentionCardBaseProps> = ({
  className,
  children,
}) => {
  const { playing } = useLocalAudio({ playing: true });
  const hash = useHash();

  const [highlight, setHighlight] = useState(false);

  const {
    channel: { channel_type },
    episode: { guid },
    mentions,
    insights,
    removedSource,
    removedMentionIds,
  } = useMentionCard();

  const title_mention_uuid =
    mentions?.find((m) => m.context === MENTION_TYPE.EPISODE_NAME)?.id || null;
  const description_mention_uuid =
    mentions?.find((m) => m.context === MENTION_TYPE.EPISODE_DESCRIPTION)?.id ||
    null;

  const mentionCardRef = useRef<HTMLDivElement>(null);

  // If a hash is present in the URL, focuses the matching mention
  useEffect(() => {
    const mention_uuids: Array<string | null> =
      mentions?.map((m) => m.id) || [];
    // adding meta-hits to uuids-array, if any.
    mention_uuids.push(title_mention_uuid, description_mention_uuid);

    if (mention_uuids.includes(hash) && mentionCardRef.current) {
      // Offset scrolling by a timeout to allow the page to render first.
      // Does not work in chrome without it.
      setTimeout(() => {
        mentionCardRef.current &&
          mentionCardRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
      }, 50);

      setHighlight(true);

      const t = setTimeout(() => {
        setHighlight(false);
      }, HIGHLIGHT_DURATION);

      return () => clearTimeout(t);
    }
  }, [hash, description_mention_uuid, title_mention_uuid, mentions]);

  const isHighreachmention = insights?.some(
    (insight) => insight.type === INSIGHT_TYPE.HIGH_REACH_MENTION
  );

  const allMentionsRemoved = removedMentionIds.length === mentions.length;

  return (
    <div
      ref={mentionCardRef}
      key={`episode-${guid}`}
      id={`episode-${guid}`}
      data-episode-id={`episode-${guid}`}
      className={cx(
        className,
        styles.root,
        "episode", // for tests only.
        "episode-container", // for tests only. Should be moved to data-testid, but is used in 50 places right now ...
        `type-${channel_type}`,
        {
          [styles.playing]: playing,
          [styles.highlighted]: highlight,
          [styles.highreachmention]: isHighreachmention,
        }
      )}
      data-testid="episode-container"
    >
      {children}
      {removedSource || allMentionsRemoved ? <UndoRemovedAll /> : null}
    </div>
  );
};
export default MentionCardBase;
